/** Native select. Native because it is keyboard- and screen-reader-correct on every platform
 *  without a thousand lines of listbox code, and because a bank workstation may be old. */

import { forwardRef, useId, type SelectHTMLAttributes } from "react";

import { cx } from "./cx";

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  options: SelectOption[];
  /** Shown under the field and announced. Presence also marks the field invalid. */
  error?: string;
  /** Standing guidance under the field, the way `Input` and `Textarea` have it. */
  hint?: string;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, options, error, hint, className, id, ...rest },
  ref,
) {
  const generated = useId();
  const fieldId = id ?? generated;
  const hintId = hint ? `${fieldId}-hint` : undefined;

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={fieldId} className="text-label-md text-textPrimary">
        {label}
      </label>
      <select
        ref={ref}
        id={fieldId}
        aria-invalid={error ? true : undefined}
        aria-describedby={hintId}
        className={cx(
          "rounded-sm border bg-surfaceRaised px-3 py-2 text-body-md text-textPrimary",
          "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary",
          error ? "border-statusDanger" : "border-borderStrong",
          className,
        )}
        {...rest}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {hint ? (
        <span id={hintId} className="text-label-md text-textMuted">
          {hint}
        </span>
      ) : null}
      {error ? (
        <span role="alert" className="text-label-md text-statusDanger">
          {error}
        </span>
      ) : null}
    </div>
  );
});
